import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import { RED_TOWER_PARTS, TOWER_CHECKLIST_INTRO } from "../../shared-pdf/data/sf004TowerParts";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const API_PATH = "/api/sf-004-j-red-tower-parts-checklist";
const FORM_TITLE = "SF-004-J RED Tower Parts Checklist";
const PARTS = RED_TOWER_PARTS;

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function parseStoredDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const d = new Date(parseInt(dmy[3], 10), parseInt(dmy[2], 10) - 1, parseInt(dmy[1], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const d = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeImageSource(value, fallbackMime = "image/png") {
  if (!value) return "";
  let src = value;
  if (Array.isArray(src)) src = src[0];
  if (src == null) return "";
  if (typeof src !== "string") src = String(src);
  src = src.trim();
  if (!src) return "";
  if (src.startsWith("data:image")) return src;
  if (/^(https?:)?\/\//i.test(src)) return src;
  const compact = src.replace(/\s+/g, "").replace(/,+$/, "");
  if (!compact) return "";
  return `data:${fallbackMime};base64,${compact}`;
}

function getPdfImageType(imageSrc, fallbackType = "PNG") {
  if (!imageSrc || typeof imageSrc !== "string") return fallbackType;
  const m = imageSrc.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/i);
  const subtype = m?.[1]?.toLowerCase();
  if (subtype === "png") return "PNG";
  if (subtype === "jpg" || subtype === "jpeg") return "JPEG";
  if (subtype === "webp") return "WEBP";
  return fallbackType;
}

// The backend already fills every key, but records imported from the app (or
// edited before a part existed) may be missing some — default those to false.
function ensureParts(value) {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const out = {};
  PARTS.forEach(({ key }) => {
    out[key] = raw[key] === true;
  });
  return out;
}

const GRID_LABEL = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "8px 10px",
  fontWeight: 700,
  color: THEME,
  background: "#f8f1f4",
  verticalAlign: "middle",
};
const GRID_FIELD = {
  border: "1px solid #cbd5e1",
  fontSize: 12,
  padding: "6px 8px",
  background: "#fff",
  verticalAlign: "middle",
};
const SECTION_BAR = {
  background: THEME,
  color: "#fff",
  fontWeight: 700,
  fontSize: 13,
  padding: "8px 10px",
};

function renderSignatureBox(value) {
  return (
    <div
      style={{
        border: "1px dashed #8b1634",
        borderRadius: 8,
        background: "#fbeef2",
        minHeight: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
      }}
    >
      {value ? (
        <img
          src={normalizeImageSource(value, "image/png")}
          alt="Signature"
          style={{ maxWidth: "100%", maxHeight: 48, objectFit: "contain" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      ) : (
        <span style={{ color: "#999", fontSize: 11 }}>No signature</span>
      )}
    </div>
  );
}

export default function Sf004JRedTowerPartsChecklistPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    fetchRecords();
    fetchAppUsers();
  }, []);

  async function fetchRecords() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRecords(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = records.filter((r) => {
    const fields = [r.engineerName, r.anyComments].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((f) => f.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const getUserName = (record) => {
    const uid = record?.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
      return full || uid || "—";
    }
    return addedByName(record) || uid || "—";
  };

  const handleEdit = (r) => {
    setEditForm({ ...r, parts: ensureParts(r?.parts), formUuid: ensureFormUuid(r?.formUuid) });
    setShowEditModal(true);
  };

  const togglePart = (key) =>
    setEditForm((prev) => {
      const parts = ensureParts(prev?.parts);
      return { ...prev, parts: { ...parts, [key]: !parts[key] } };
    });

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH + "/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          parts: ensureParts(editForm.parts),
          anyComments: editForm.anyComments,
          engineerName: editForm.engineerName,
          dateTowerTaken: editForm.dateTowerTaken,
          dateTowerReturned: editForm.dateTowerReturned,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchRecords();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      toast.error("Invalid record id");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + API_PATH + "/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter((f) => f._id !== deleteId));
        toast.success("Record deleted!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const generatePDF = async (record, options = {}) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const primaryColor = [139, 22, 52];
    const headerHeight = 30;
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    await new Promise((resolve) => {
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.onload = function onLoad() {
        try {
          const logoWidth = 70;
          const logoHeight = logoWidth * (logoImg.height / logoImg.width);
          const canvas = document.createElement("canvas");
          const scale = 2.5;
          canvas.width = logoImg.width * scale;
          canvas.height = logoImg.height * scale;
          const ctx = canvas.getContext("2d");
          ctx.scale(scale, scale);
          ctx.drawImage(logoImg, 0, 0);
          doc.addImage(canvas.toDataURL("image/png", 1.0), "PNG", 10, (headerHeight - logoHeight) / 2, logoWidth, logoHeight, "", "NONE");
        } catch {}
        resolve();
      };
      logoImg.onerror = () => resolve();
      logoImg.src = onsiteproLogo;
    });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text(FORM_TITLE, 90, 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 20.5);

    const innerW = pageWidth - 20;
    const labelCell = (t) => ({ content: t, styles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" } });
    const val = (t) => ({ content: t || "", styles: { fontStyle: "bold" } });
    const common = {
      styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, overflow: "linebreak", valign: "middle", lineColor: [203, 213, 225], lineWidth: 0.2 },
      bodyStyles: { fontStyle: "bold" },
      theme: "grid",
      tableWidth: innerW,
      margin: { left: 10, right: 10 },
    };

    autoTable(doc, {
      ...common,
      startY: headerHeight + 8,
      body: [[{ content: TOWER_CHECKLIST_INTRO, styles: { fontSize: 8, fontStyle: "normal" } }]],
    });

    const parts = ensureParts(record.parts);
    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      head: [["Part", "Present / Taken"]],
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
      body: PARTS.map(({ key, label }) => [
        label,
        { content: parts[key] ? "Yes" : "No", styles: { halign: "center" } },
      ]),
      columnStyles: { 1: { cellWidth: 40 } },
    });

    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      body: [
        [labelCell("Any Comments (missing or broken parts)")],
        [{ content: record.anyComments || "", styles: { minCellHeight: 18 } }],
      ],
    });

    autoTable(doc, {
      ...common,
      startY: doc.lastAutoTable.finalY + 3,
      body: [
        [labelCell("Engineer Name:"), val(record.engineerName)],
        [labelCell("Engineer Signature:"), { content: "", styles: { minCellHeight: 20 } }],
        [labelCell("Date Tower Taken:"), val(toDdMmYyyy(record.dateTowerTaken))],
        [labelCell("Date Tower Returned:"), val(toDdMmYyyy(record.dateTowerReturned))],
      ],
      columnStyles: { 0: { cellWidth: 50 } },
      didDrawCell: (d) => {
        if (d.section === "body" && d.row.index === 1 && d.column.index === 1) {
          const src = normalizeImageSource(record.engineerSignature, "image/png");
          if (src) {
            try {
              const pr = doc.getImageProperties(src);
              const ratio = Math.min((d.cell.width - 4) / pr.width, (d.cell.height - 3) / pr.height);
              doc.addImage(src, getPdfImageType(src), d.cell.x + (d.cell.width - pr.width * ratio) / 2, d.cell.y + (d.cell.height - pr.height * ratio) / 2, pr.width * ratio, pr.height * ratio, undefined, "NONE");
            } catch {}
          }
        }
      },
    });

    const totalPagesPdf = doc.getNumberOfPages();
    for (let pg = 1; pg <= totalPagesPdf; pg += 1) {
      doc.setPage(pg);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${pg} of ${totalPagesPdf}`, pageWidth - 30, pageHeight - 8);
    }

    const safeName = String(record.engineerName || "tower-parts-checklist").replace(/[\\/:"*?<>|]+/g, "-");
    const shouldUpload = options.uploadOnly === true;
    const fileName = shouldUpload
      ? buildSharePointFileName([
          record.engineerName || addedByName(record),
          "RED Tower",
          toDdMmYyyy(record.dateTowerTaken),
        ])
      : `SF004J_${safeName}.pdf`;

    if (shouldUpload) {
      try {
        setUploadingKey(record._id || "");
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify({ fileName, fileBase64: dataUri, uploadContext: "hseq-forms-tablets", uploadSubFolder: FORM_TITLE }),
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try { const out = await res.json(); msg = out?.message || msg; } catch {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success(`${FORM_TITLE} PDF generated!`);
    }
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>{FORM_TITLE}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button type="button" onClick={() => setShowImportModal(true)} style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Engineer</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Parts Present</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date Taken</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Date Returned</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const parts = ensureParts(r.parts);
                  const present = PARTS.filter(({ key }) => parts[key]).length;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{r.engineerName || "—"}</td>
                      <td style={{ color: present === PARTS.length ? "#16a34a" : "#b45309", fontSize: 14, padding: 16, fontWeight: 600 }}>{present} / {PARTS.length}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.dateTowerTaken)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.dateTowerReturned)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.updatedAt || r.createdAt)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button type="button" onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const busy = uploadingKey === (r._id || "");
                            return (
                              <button
                                type="button"
                                onClick={() => { if (!busy) generatePDF(r, { uploadOnly: true }); }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button type="button" onClick={() => handleDelete(r._id || r.id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button type="button" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1040, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit {FORM_TITLE}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>

                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>TOWER PARTS</div>
                  <div style={{ padding: "10px 12px", background: "#fff", fontSize: 11, color: "#475569" }}>{TOWER_CHECKLIST_INTRO}</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ ...GRID_LABEL, background: THEME, color: "#fff" }}>Part</th>
                        <th style={{ ...GRID_LABEL, background: THEME, color: "#fff", width: "22%", textAlign: "center" }}>Present / Taken</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PARTS.map(({ key, label }) => (
                        <tr key={key}>
                          <td style={GRID_FIELD}>{label}</td>
                          <td style={{ ...GRID_FIELD, textAlign: "center" }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{ cursor: "pointer" }}
                              checked={ensureParts(editForm.parts)[key]}
                              onChange={() => togglePart(key)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ border: "1px solid #cbd5e1", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
                  <div style={SECTION_BAR}>SIGN OFF</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                    <tbody>
                      <tr>
                        <td style={{ ...GRID_LABEL, width: "32%" }}>Any Comments (missing or broken parts):</td>
                        <td style={GRID_FIELD}><textarea className="form-control form-control-sm" rows={3} value={editForm.anyComments || ""} onChange={(e) => setEditForm({ ...editForm, anyComments: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Engineer Name:</td>
                        <td style={GRID_FIELD}><input className="form-control form-control-sm" value={editForm.engineerName || ""} onChange={(e) => setEditForm({ ...editForm, engineerName: e.target.value })} /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Engineer Signature:</td>
                        <td style={GRID_FIELD}>{renderSignatureBox(editForm.engineerSignature)}</td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date Tower Taken:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.dateTowerTaken)} onChange={(d) => setEditForm({ ...editForm, dateTowerTaken: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                      </tr>
                      <tr>
                        <td style={GRID_LABEL}>Date Tower Returned:</td>
                        <td style={GRID_FIELD}><DatePicker selected={parseStoredDate(editForm.dateTowerReturned)} onChange={(d) => setEditForm({ ...editForm, dateTowerReturned: d ? dateToYmd(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" placeholderText="Select date" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={`Upload ${FORM_TITLE} File`}
        importPath="sf-004-j-red-tower-parts-checklist/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete {FORM_TITLE}</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this record?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
